<!-- BaseModal.vue - Unified Reusable Modal Component -->
<template>
  <Teleport to="body">
    <Transition name="modal-fade">
      <div
        v-if="isVisible"
        class="fixed inset-0 z-50 overflow-y-auto max-[730px]:!overflow-hidden"
        :aria-labelledby="ariaLabelledby"
        role="dialog"
        aria-modal="true"
      >
        <!-- Background overlay -->
        <div
          class="flex items-end justify-center min-h-screen pt-0 px-0 pb-0 text-center sm:block sm:p-0 max-[730px]:!block max-[730px]:!p-0"
        >
          <Transition name="overlay-fade">
            <div
              v-if="isVisible"
              class="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm transition-all duration-300"
              aria-hidden="true"
              @click="handleBackdropClick"
            ></div>
          </Transition>

          <!-- This element is to trick the browser into centering the modal contents. -->
          <span class="hidden sm:inline-block sm:align-middle sm:h-screen max-[730px]:!hidden" aria-hidden="true"
            >&#8203;</span
          >

          <!-- Modal panel -->
          <Transition name="modal-slide">
            <div
              v-if="isVisible"
              class="relative inline-block align-bottom bg-white dark:bg-gray-800 text-left overflow-hidden shadow-2xl transform transition-all w-full sm:my-8 sm:align-middle max-[730px]:!flex max-[730px]:!flex-col max-[730px]:!fixed max-[730px]:!inset-0 max-[730px]:!m-0 max-[730px]:!h-screen max-[730px]:!max-h-screen max-[730px]:!w-screen max-[730px]:!align-baseline"
              :class="[
                modalSize,
                modalType === 'offcanvas' ? 'fixed inset-y-0 right-0 h-full max-w-full pl-10' : 'sm:rounded-3xl min-h-screen sm:min-h-0 max-[730px]:!rounded-none max-[730px]:!min-h-screen'
              ]"
            >
              <!-- Header -->
              <div 
                v-if="showHeader"
                :class="[
                  'px-4 py-4 sm:px-6',
                  headerGradient,
                  modalType === 'offcanvas' ? 'rounded-tl-3xl' : ''
                ]"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center">
                    <!-- Icon -->
                    <div
                      v-if="icon"
                      class="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full transition-colors"
                      :class="headerTheme.iconBg"
                    >
                      <component :is="icon" class="h-6 w-6" :class="headerTheme.icon" />
                    </div>
                    
                    <!-- Title -->
                    <div :class="icon ? 'ml-4' : ''">
                      <h3 
                        class="text-xl"
                        :class="headerTheme.title"
                        :id="ariaLabelledby"
                      >
                        {{ title }}
                      </h3>
                      <p 
                        v-if="subtitle" 
                        class="text-sm mt-1"
                        :class="headerTheme.subtitle"
                      >
                        <slot name="subtitle">{{ subtitle }}</slot>
                      </p>
                    </div>
                  </div>
                  
                  <!-- Close button -->
                  <button
                    v-if="showCloseButton"
                    @click="$emit('close')"
                    class="p-2 rounded-lg transition-colors"
                    :class="headerTheme.closeBtn"
                    :aria-label="closeButtonLabel"
                  >
                    <X class="w-5 h-5" />
                  </button>
                </div>
              </div>

              <!-- Content -->
              <div 
                :class="[
                  contentPadding,
                  scrollable ? 'overflow-y-auto' : '',
                  modalType === 'offcanvas' && showHeader ? 'flex-1' : '',
                  maxHeight,
                  'max-[730px]:!flex-1 max-[730px]:!overflow-y-auto max-[730px]:!max-h-none'
                ]"
              >
                <slot />
              </div>

              <!-- Footer -->
              <div 
                v-if="showFooter"
                class="bg-gray-50 dark:bg-gray-700 px-4 py-4 sm:px-6 border-t border-gray-200 dark:border-gray-600"
              >
                <div class="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-3 space-y-3 space-y-reverse sm:space-y-0">
                  <slot name="footer" />
                </div>
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { X } from 'lucide-vue-next'

export interface Props {
  isVisible: boolean
  title?: string
  subtitle?: string
  icon?: any
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | 'full'
  type?: 'default' | 'danger' | 'success' | 'warning' | 'info' | 'secondary' | 'forest' | 'primary'
  modalType?: 'modal' | 'offcanvas'
  showHeader?: boolean
  showFooter?: boolean
  showCloseButton?: boolean
  closableByBackdrop?: boolean
  scrollable?: boolean
  contentPadding?: string
  maxHeight?: string
  ariaLabelledby?: string
  closeButtonLabel?: string
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  subtitle: '',
  icon: undefined,
  size: 'lg',
  type: 'default',
  modalType: 'modal',
  showHeader: true,
  showFooter: false,
  showCloseButton: true,
  closableByBackdrop: true,
  scrollable: true,
  contentPadding: 'px-4 py-4 sm:px-6 sm:py-6',
  maxHeight: 'max-h-[90vh]',
  ariaLabelledby: 'modal-title',
  closeButtonLabel: 'Close modal'
})

const emit = defineEmits<{
  close: []
}>()

// Computed properties for dynamic styling
const modalSize = computed(() => {
  const sizes = {
    sm: 'max-w-full sm:max-w-sm',
    md: 'max-w-full sm:max-w-md', 
    lg: 'max-w-full sm:max-w-lg',
    xl: 'max-w-full sm:max-w-xl',
    '2xl': 'max-w-full max-[730px]:!max-w-full max-[730px]:!w-full max-[730px]:!h-full max-[730px]:!m-0 max-[730px]:!rounded-none sm:max-w-2xl',
    '3xl': 'max-w-full sm:max-w-3xl',
    '4xl': 'max-w-full sm:max-w-4xl',
    full: 'max-w-full'
  }
  return sizes[props.size]
})

const headerGradient = computed(() => {
  const gradients = {
    default: 'bg-[#0b6623]',
    forest: 'bg-[#0b6623]',
    secondary: 'bg-[#0275b1]',
    danger: 'bg-danger',
    success: 'bg-success',
    warning: 'bg-warning',
    info: 'bg-info',
    primary: 'bg-primary'
  }
  return gradients[props.type] || gradients.default
})

const isLightHeader = computed(() => {
  return props.type === 'primary' || props.type === 'warning'
})

const headerTheme = computed(() => {
  if (isLightHeader.value) {
    return {
      title: 'text-gray-900 font-bold tracking-tight',
      subtitle: 'text-gray-800/90 font-medium',
      icon: 'text-gray-900',
      iconBg: 'bg-black/10',
      closeBtn: 'text-gray-900 hover:bg-black/10',
    }
  }

  return {
    title: 'text-white font-semibold',
    subtitle: 'text-white/80',
    icon: 'text-white',
    iconBg: 'bg-white/20 backdrop-blur-sm',
    closeBtn: 'text-white hover:bg-white/20',
  }
})

// Handle backdrop click
const handleBackdropClick = () => {
  if (props.closableByBackdrop) {
    emit('close')
  }
}
</script>

<style scoped>
/* Animation styles */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.overlay-fade-enter-active,
.overlay-fade-leave-active {
  transition: all 0.2s ease;
}

.overlay-fade-enter-from,
.overlay-fade-leave-to {
  opacity: 0;
  backdrop-filter: blur(0px);
}

.modal-slide-enter-active {
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.modal-slide-leave-active {
  transition: all 0.3s ease;
}

.modal-slide-enter-from {
  opacity: 0;
  transform: translateY(20px) scale(0.95);
}

.modal-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px) scale(1.02);
}

/* Offcanvas specific animations */
.offcanvas-slide-enter-active,
.offcanvas-slide-leave-active {
  transition: transform 0.3s ease-in-out;
}

.offcanvas-slide-enter-from,
.offcanvas-slide-leave-to {
  transform: translateX(100%);
}


</style>
